/**
 * FuelCostEstimator - Compact fuel cost badge with expandable settings
 * Shows estimated gas cost based on route distance, MPG, and gas price
 */
import React, { useState, useEffect, useRef } from 'react';
import { Fuel, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const STORAGE_KEY = 'fuelEstimatorSettings';

// Default values
const DEFAULT_MPG = 25;
const DEFAULT_GAS_PRICE = 3.50;

const FuelCostEstimator = ({ distanceMiles }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [mpg, setMpg] = useState(DEFAULT_MPG);
  const [gasPrice, setGasPrice] = useState(DEFAULT_GAS_PRICE);
  const [editMpg, setEditMpg] = useState('');
  const [editGasPrice, setEditGasPrice] = useState('');
  const panelRef = useRef(null);

  // Load settings from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const { mpg: savedMpg, gasPrice: savedGasPrice } = JSON.parse(saved);
        if (savedMpg) setMpg(savedMpg);
        if (savedGasPrice) setGasPrice(savedGasPrice);
      }
    } catch (e) {
      console.error('Error loading fuel settings:', e);
    }
  }, []);

  // Initialize edit fields when expanding
  useEffect(() => {
    if (isExpanded) {
      setEditMpg(mpg.toString());
      setEditGasPrice(gasPrice.toFixed(2));
    }
  }, [isExpanded, mpg, gasPrice]);

  // Close panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        setIsExpanded(false);
      }
    };

    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isExpanded]);

  // Calculate fuel cost
  const fuelCost = distanceMiles && mpg > 0 
    ? (distanceMiles / mpg) * gasPrice 
    : 0;

  // Save settings
  const handleSave = () => {
    const newMpg = parseFloat(editMpg);
    const newGasPrice = parseFloat(editGasPrice);

    if (isNaN(newMpg) || newMpg <= 0) {
      return;
    }
    if (isNaN(newGasPrice) || newGasPrice <= 0) {
      return;
    }

    setMpg(newMpg);
    setGasPrice(newGasPrice);

    // Persist to localStorage
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        mpg: newMpg,
        gasPrice: newGasPrice
      }));
    } catch (e) {
      console.error('Error saving fuel settings:', e);
    }

    setIsExpanded(false);
  };

  // Handle Enter key to save
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSave();
    }
    if (e.key === 'Escape') {
      setIsExpanded(false);
    }
  };

  return (
    <div className="relative" ref={panelRef}>
      {/* Main Badge - Clickable to expand */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-1.5 bg-amber-500/90 backdrop-blur-sm text-white px-3 py-1.5 rounded-xl text-sm font-medium shadow-soft-lg hover:bg-amber-600/90 transition-colors"
        data-testid="fuel-cost-badge"
        title="Estimated fuel cost - click to edit settings"
      >
        <Fuel className="h-4 w-4" />
        <span>${fuelCost.toFixed(2)}</span>
        {isExpanded ? (
          <ChevronUp className="h-3 w-3 ml-0.5" />
        ) : (
          <ChevronDown className="h-3 w-3 ml-0.5" />
        )}
      </button>

      {/* Expandable Settings Panel */}
      {isExpanded && (
        <div className="absolute top-full right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-stone-200 p-4 z-50">
          <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-100">
            <Fuel className="h-4 w-4 text-amber-500" />
            <span className="font-medium text-stone-800 text-sm">Fuel Cost Settings</span>
          </div>

          <div className="space-y-3">
            {/* MPG Input */}
            <div>
              <Label htmlFor="mpg-input" className="text-xs text-stone-600 mb-1 block">
                Vehicle MPG
              </Label>
              <Input
                id="mpg-input"
                type="number"
                value={editMpg}
                onChange={(e) => setEditMpg(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="25"
                min="1"
                max="100"
                step="0.1"
                className="h-9 text-sm"
                data-testid="mpg-input"
              />
            </div>

            {/* Gas Price Input */}
            <div>
              <Label htmlFor="gas-price-input" className="text-xs text-stone-600 mb-1 block">
                Gas Price ($/gal)
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm">$</span>
                <Input
                  id="gas-price-input"
                  type="number"
                  value={editGasPrice}
                  onChange={(e) => setEditGasPrice(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="3.50"
                  min="0.01"
                  max="20"
                  step="0.01"
                  className="h-9 text-sm pl-7"
                  data-testid="gas-price-input"
                />
              </div>
            </div>

            {/* Cost Preview */}
            <div className="bg-amber-50 rounded-xl p-2 text-center">
              <div className="text-xs text-amber-700 mb-0.5">Estimated Cost</div>
              <div className="text-lg font-bold text-amber-600">
                ${(distanceMiles / (parseFloat(editMpg) || mpg) * (parseFloat(editGasPrice) || gasPrice)).toFixed(2)}
              </div>
              <div className="text-xs text-stone-500 mt-0.5">
                {distanceMiles} mi / {editMpg || mpg} mpg x ${editGasPrice || gasPrice.toFixed(2)}/gal
              </div>
            </div>

            {/* Save Button */}
            <Button
              onClick={handleSave}
              size="sm"
              className="w-full bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="save-fuel-settings"
            >
              Save Settings
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FuelCostEstimator;
